import { useState } from "react";
import { toast } from "sonner";
import { CalendarDays, Clock, Loader2, History, Pencil, Eraser } from "lucide-react";
import { setSaleDate, clearSaleDate, fmtSaleDate, NOTIFY_STATUS, errMsg } from "@/lib/applyApi";
import { timeLabel } from "@/lib/mountainTime";
import { AdDatesFields, computeDates, startOverride, fetchStart, START_HOURS } from "@/components/schedule/AdDates";
import { Button } from "@/components/ui/button";
import { AuctionClose } from "@/lib/brand";
import { fmtMT } from "@/lib/mountainTime";
import { DatePick, toDate, iso } from "@/components/adtemplate/DatePick";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const CH = { email: "Email", sms: "Text" };
const LBL = "block text-[11px] uppercase tracking-wider text-stone-400 font-semibold";

function NotifyBadge({ n, testId }) {
  if (!n) return null;
  const [l, tone] = NOTIFY_STATUS[n.status] || [n.status, "text-stone-600 bg-stone-50 border-stone-200"];
  return (
    <div data-testid={testId} className={`mt-2 text-xs border rounded p-2 ${tone}`}>
      <div className="font-semibold">Seller notification: {l}</div>
      <div className="mt-0.5">{Object.entries(n.channels || {}).map(([c, s]) => `${CH[c] || c}: ${(NOTIFY_STATUS[s] || [s])[0].split(" — ")[0]}`).join(" · ")}</div>
    </div>
  );
}

function ScheduleHistory({ history }) {
  if (!history?.length) return null;
  return (
    <div className="mt-3">
      <div className="text-xs font-semibold text-stone-600 flex items-center gap-1 mb-1"><History className="h-3 w-3" /> Scheduling history</div>
      <ol data-testid="sale-history" className="space-y-1 text-xs text-stone-600">
        {[...history].reverse().map((h) => (
          <li key={h.at}>
            {h.action === "cleared" ? <><strong>Schedule cleared</strong>{h.from ? ` (was ${fmtSaleDate(h.from)})` : ""}</>
              : <>{h.from && h.from !== h.to ? `${fmtSaleDate(h.from)} → ` : h.from ? "Ends " : "Scheduled "}<strong>{fmtSaleDate(h.to)}</strong>
                {h.start ? <div>Bidding Starts {fmtSaleDate(h.start)}{h.start_time ? ` · ${timeLabel(h.start_time)}` : ""}{h.start_auto ? ` (auto ${h.window_hours || 36}h)` : h.start === h.facebook_post_date ? " (posting date)" : " (manual)"}</div> : null}
                {h.facebook_post_date ? <div>Facebook Post {fmtSaleDate(h.facebook_post_date)}{h.facebook_post_date_auto === false ? " (manual)" : " (auto)"}</div> : null}</>}
            {h.set_by === "seller" ? " (seller)" : ""}{h.before_approval ? " (seller not notified)" : ""}
            <div className="text-stone-400">{fmtMT(h.at)} · {h.by}</div>
          </li>
        ))}
      </ol>
    </div>
  );
}

function ClearSchedule({ onConfirm, busy }) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button data-testid="sale-clear-schedule" variant="ghost" disabled={busy} className="w-full h-10 rounded-full text-red-700 hover:text-red-800 hover:bg-red-50">
          <Eraser className="h-4 w-4 mr-2" />Clear Schedule
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent data-testid="sale-clear-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Clear this sale schedule?</AlertDialogTitle>
          <AlertDialogDescription>
            Scheduled Facebook Post Date, Bidding Starts, Bidding Ends and Website Publish Date will be removed everywhere. The horse, approval status, pipeline stage,
            agreements and ads are not changed, and the seller is not notified.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="sale-clear-cancel">Keep Schedule</AlertDialogCancel>
          <AlertDialogAction data-testid="sale-clear-confirm" onClick={onConfirm} className="bg-red-700 hover:bg-red-800">Clear Schedule</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// Older schedules saved before the Facebook date existed: same auto rule as the listing date.
export const fbDate = (s) => (s?.facebook_post_date !== undefined && s?.facebook_post_date !== null
  ? [s.facebook_post_date, s.facebook_post_date_auto !== false]
  : s?.listing_date_auto !== false && s?.listing_date ? [s.listing_date, true] : [null, true]);

function CurrentSchedule({ s }) {
  if (!s?.close_date) {
    return <div data-testid="sale-not-scheduled" className="text-sm font-semibold text-stone-500">Sale Not Scheduled</div>;
  }
  const [fb, fbAuto] = fbDate(s);
  return (
    <>
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
        <dt className="text-stone-500">Scheduled Facebook Post Date</dt>
        <dd data-testid="sale-current-fb" className="font-bold text-stone-900">
          {fb ? fmtSaleDate(fb) : "Not set yet (needs weeks advertised)"}
          <span data-testid="sale-current-fb-mode" className="ml-1 text-[11px] font-normal text-stone-500">({fbAuto ? `Automatic${s.advertised_weeks ? ` · close − ${s.advertised_weeks} wk` : ""}` : "Manual"})</span>
        </dd>
        <dt className="text-stone-500">Advertising Duration</dt>
        <dd data-testid="sale-current-duration" className="font-semibold text-stone-900">
          {s.advertised_weeks ? `${s.advertised_weeks} Week${s.advertised_weeks === 1 ? "" : "s"} (contract)` : s.schedule_weeks ? `${s.schedule_weeks} Week${s.schedule_weeks === 1 ? "" : "s"} (scheduling override)` : "Not Set"}
        </dd>
        <dt className="text-stone-500">Bidding Starts</dt>
        <dd data-testid="sale-current-start" className="font-semibold text-stone-900">
          {s.start_date ? `${fmtSaleDate(s.start_date)}${s.start_time_label ? ` · ${s.start_time_label}` : ""}` : "Not set"}
          {s.start_date && <span className="ml-1 text-[11px] font-normal text-stone-500">{s.start_auto ? `(auto ${s.window_hours || 36}h)` : s.start_date === fbDate(s)[0] ? "(posting date)" : "(manual)"}</span>}
        </dd>
        <dt className="text-stone-500">Bidding Ends</dt>
        <dd data-testid="sale-current-date" className="font-semibold text-stone-900">{fmtSaleDate(s.close_date)} · {s.close_time_label || <AuctionClose />}</dd>
      </dl>
      {s.listing_date && <div data-testid="sale-current-listing" className="mt-1 text-xs text-stone-600">Website Publish Date {fmtSaleDate(s.listing_date)}{s.listing_date_auto ? " (auto from weeks advertised)" : " (set manually)"} · goes live on the website that day once the ad is approved and published</div>}
    </>
  );
}

export function SaleScheduling({ app, onChange, readOnly = false }) {
  const s = app.sale_schedule;
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState({});
  const [listing, setListing] = useState();
  const [busy, setBusy] = useState(false);
  const contractWeeks = app.contract_weeks || null;
  const approved = app.status === "approved";
  const schedulable = ["submitted", "under_review", "more_info_requested", "approved"].includes(app.status) && !readOnly;
  const today = new Date(); today.setHours(0, 0, 0, 0);

  // Edit always starts from the saved (authoritative) schedule, so nothing is overwritten by stale picks.
  const savedV = () => {
    const [fb, fbAuto] = fbDate(s);
    return { close: toDate(s?.close_date), post: fbAuto ? undefined : toDate(fb), weeks: s?.schedule_weeks ? String(s.schedule_weeks) : "",
      ...startOverride(s?.close_date, s?.start_date, s?.start_time) };
  };
  const beginEdit = async () => {
    await fetchStart(s?.close_date);  // compare the saved start against the server's 72h rule
    setV(savedV());
    setListing(s?.listing_date_auto === false ? toDate(s?.listing_date) : undefined);
    setEditing(true);
  };
  const d = computeDates(v, contractWeeks);
  const sk = (x) => [x.close && iso(x.close), x.post && iso(x.post), x.start && iso(x.start), x.stime, x.weeks].join("|");
  const unchanged = editing && s?.close_date && sk(v) === sk(savedV())
    && (listing ? iso(listing) : null) === (s.listing_date_auto === false ? s.listing_date : null);

  const save = async () => {
    setBusy(true);
    try {
      const weeksBody = contractWeeks ? {} : { schedule_weeks: d.weeks || 0 };
      const startBody = d.startManual ? { start_date: d.startDate, start_time: d.startTime } : { start_auto: true, window_hours: START_HOURS };
      onChange(await setSaleDate(app.id, d.close, { ...startBody, close_time: "20:00", ...weeksBody,
        listing_date: listing ? iso(listing) : "auto", facebook_post_date: v.post ? iso(v.post) : "auto" }));
      toast.success(approved ? (s?.close_date ? "Sale schedule updated — seller message posted" : "Sale scheduled — seller message posted")
        : "Sale schedule saved. The seller isn't notified until the application is approved.");
      setEditing(false);
    } catch (e) {
      toast.error(errMsg(e, "Could not save sale date"));
    } finally {
      setBusy(false);
    }
  };
  const clear = async () => {
    setBusy(true);
    try {
      onChange(await clearSaleDate(app.id));
      toast.success("Sale schedule cleared. The seller was not notified.");
      setEditing(false);
    } catch (e) {
      toast.error(errMsg(e, "Could not clear the schedule"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section data-testid="sale-scheduling" className="bg-white border border-stone-200 rounded-xl p-4">
      <h2 className="font-semibold text-stone-900 mb-1 flex items-center gap-2"><CalendarDays className="h-4 w-4 text-stone-600" /> Sale Scheduling</h2>
      <p className="text-[11px] text-stone-400 mb-3 flex items-center gap-1"><Clock className="h-3 w-3" /> Bidding closes <AuctionClose /> (daylight saving handled automatically)</p>
      <CurrentSchedule s={s} />
      {s?.set_by === "seller" && s?.close_date && <div data-testid="sale-seller-selected-badge" className="mt-1 inline-flex text-[11px] font-semibold rounded-full border border-blue-200 bg-blue-50 text-blue-800 px-2 py-0.5">Seller-selected dates — review &amp; override if needed</div>}
      {!approved && schedulable && <p data-testid="sale-pre-approval-note" className="mt-2 text-xs text-stone-500">You can schedule before approving. Saving doesn't approve, notify the seller or move the horse.</p>}
      {schedulable && !editing && (
        <Button data-testid="sale-edit-schedule" variant="outline" onClick={beginEdit} className="mt-3 w-full h-11 rounded-full">
          <Pencil className="h-4 w-4 mr-2" />{s?.close_date ? "Edit Schedule" : "Schedule Sale"}
        </Button>
      )}
      {schedulable && editing ? (
        <div data-testid="sale-edit-form" className="mt-3 space-y-2">
          <AdDatesFields v={v} setV={setV} contractWeeks={contractWeeks} weeksSource="signed contract" testPrefix="sale" calendarMode={app.is_test ? "test" : "live"} />
          <span className={LBL}>Website Publish Date (optional)</span>
          <DatePick testId="sale-listing-picker" value={listing} onChange={setListing} placeholder="Website publish date (auto: close date − weeks)" disabledBefore={today} />
          {listing && <button type="button" data-testid="sale-listing-auto" onClick={() => setListing(undefined)} className="text-xs text-stone-700 underline">Use automatic website publish date</button>}
          <div className="grid grid-cols-2 gap-2 pt-1">
            <Button data-testid="sale-date-save" onClick={save} disabled={busy || d.invalid || unchanged} className="h-11 rounded-full bg-amber-800 hover:bg-amber-900 text-white">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
            </Button>
            <Button data-testid="sale-edit-cancel" variant="outline" onClick={() => setEditing(false)} disabled={busy} className="h-11 rounded-full">Cancel</Button>
          </div>
          {s?.close_date && <ClearSchedule onConfirm={clear} busy={busy} />}
        </div>
      ) : !schedulable && !readOnly ? (
        <p data-testid="sale-not-approved" className="mt-2 text-xs text-stone-500">This application can't be scheduled in its current status.</p>
      ) : null}
      <NotifyBadge n={s?.notification} testId="sale-notify-status" />
      <ScheduleHistory history={s?.history} />
    </section>
  );
}
